//* react
import React from 'react';

//* material-ui
import { alpha, Box, darken, lighten, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';

//* types
import { TemplatePreviewProps } from 'types/templates/templates-menu';
import { getDateParts, getFileFormatFromURL } from 'utils/helpers';
import { substituteTemplatePlaceholders } from 'utils/template-placeholders';

//* icons
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import TemplateMessage from 'views/chats/singleChat/chat/conversationWindow/messages/single-message/message-types/TemplateMessage';

//* system

const templatePreviewsByFileType = {
  pdf: '/images/file-types/pdf.png',
  docx: '/images/file-types/docx_icon.png',
  xlsx: '/images/file-types/xlsx.png',
  zip: '/images/file-types/zip.png',
};

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  originalTemplate,
  varsData,
  mediaFile,
  noUpload,
  readyTemplate,
  whatsappStyle = false,
}) => {
  const theme = useTheme();
  const { templateHeader, templateBody, templateFooter, templateButtons } = React.useMemo(() => {
    const templateHeader = originalTemplate?.components?.find((comp) => comp.type === 'HEADER');
    const templateBody = originalTemplate?.components?.find((comp) => comp.type === 'BODY');
    const templateFooter = originalTemplate?.components?.find((comp) => comp.type === 'FOOTER');
    const templateButtons = originalTemplate?.components?.find((comp) => comp.type === 'BUTTONS');

    return {
      templateHeader,
      templateBody,
      templateFooter,
      templateButtons,
    };
  }, [originalTemplate]);

  const currentDate = React.useMemo(() => {
    const dateParts = getDateParts();
    return `${dateParts.day}/${dateParts.month}/${dateParts.year} ${dateParts.hours}:${dateParts.minutes}`;
  }, []);

  const fileType = React.useMemo(
    () => mediaFile?.url && (getFileFormatFromURL(mediaFile.url) as keyof typeof templatePreviewsByFileType),
    [mediaFile],
  );

  const buttonsContainerRef = React.useRef<HTMLDivElement | null>(null);

  /** בועת תצוגה מקדימה ממותגת (לא WhatsApp) — טקסט ואייקונים בצבע ניגוד לרקע */
  //* undefined ולא false: הביטוי `!whatsappStyle && (...)` טיפס כ-string|false,
  //* ו-false אינו ערך sx חוקי (שבר את הקומפילציה של backgroundColor).
  const brandBubbleBackground = whatsappStyle
    ? undefined
    : theme.palette.mode === 'dark'
      ? alpha(darken(theme.palette.primary.main, 0.5), 0.65)
      : lighten(theme.palette.primary.main, 0.75);
  const brandBubbleUsesLightForeground =
    Boolean(brandBubbleBackground) &&
    theme.palette.getContrastText(brandBubbleBackground as string) === theme.palette.common.white;
  const brandBubbleTextColor = brandBubbleUsesLightForeground ? theme.palette.common.white : undefined;
  const brandBubbleMutedTextColor = brandBubbleUsesLightForeground
    ? alpha(theme.palette.common.white, 0.72)
    : undefined;
  const brandBubbleButtonColor = brandBubbleUsesLightForeground
    ? theme.palette.common.white
    : 'info.main';
  const brandBubbleButtonDividerColor = brandBubbleUsesLightForeground
    ? alpha(theme.palette.common.white, 0.28)
    : '#c2c2c2';

  const bubblePaperSx = whatsappStyle
    ? {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#dcf8c6',
        minWidth: 240,
        maxWidth: 325,
        p: 1.25,
        borderRadius: '12px 12px 4px 12px',
        boxShadow: theme.shadows[1],
        color: 'text.primary',
      }
    : {
        backgroundColor: brandBubbleBackground,
        minWidth: 240,
        maxWidth: 325,
        p: 1.25,
        borderRadius: ' 0 8px 8px 8px',
        ...(brandBubbleTextColor && { color: brandBubbleTextColor }),
      };

  return (
    <Box
      sx={{
        position: 'relative',
        ...(whatsappStyle
          ? {
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : '#ece5dd',
              p: 1.25,
            }
          : { pl: 1.25 }),
      }}
      style={
        whatsappStyle
          ? {
              position: 'relative',
              direction: 'rtl',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flex: 1,
              minHeight: 0,
              width: '100%',
              overflow: 'auto',
            }
          : undefined
      }
    >
      {!whatsappStyle && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 'calc(100% - 10px)',
            width: 0,
            height: 0,
            borderRight: (t) =>
              `10px solid ${
                t.palette.mode === 'dark'
                  ? alpha(darken(t.palette.primary.main, 0.5), 0.65)
                  : lighten(t.palette.primary.main, 0.75)
              }`,
            borderBottom: '15px solid transparent',
          }}
        />
      )}
      <Box
        component={whatsappStyle ? 'section' : 'div'}
        dir={whatsappStyle ? 'rtl' : undefined}
        lang={whatsappStyle ? 'he' : undefined}
        sx={{
          ...bubblePaperSx,
          ...(whatsappStyle
            ? {
                width: 'max-content',
                maxWidth: 'min(calc(100% - 8px), 420px)',
              }
            : {}),
        }}
        style={
          whatsappStyle
            ? {
                marginInlineEnd: 'auto',
                marginInlineStart: 0,
              }
            : undefined
        }
      >
        <Grid container spacing={1} sx={whatsappStyle ? { width: '100%', maxWidth: '100%' } : undefined}>
          {readyTemplate ? (
            <React.Fragment>
              <TemplateMessage
                isIncoming={false}
                text={readyTemplate}
                handleMediaPreview={() => {}}
                status={''}
                scrollToBottom={() => {}}
                mediaShouldScrollBottomRef={{
                  current: false,
                }}
                buttonsContainerRef={buttonsContainerRef}
              />
              <Grid size={12}>
                <Box ref={buttonsContainerRef}></Box>
              </Grid>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {templateHeader && (
                <Grid size={12} position={'relative'}>
                  {templateHeader.format === 'TEXT' ? (
                    <Typography
                      variant="body2"
                      fontSize={14}
                      fontWeight={700}
                      sx={{
                        wordBreak: 'break-all',
                      }}
                      style={whatsappStyle ? { textAlign: 'right', width: '100%' } : undefined}
                    >
                      {varsData?.some((param) => param.name === 'header_1')
                        ? substituteTemplatePlaceholders(templateHeader.text, varsData, 'header')
                        : templateHeader.text}
                    </Typography>
                  ) : mediaFile?.url ? (
                    mediaFile.type === 'video' ? (
                      <video
                        src={mediaFile?.url}
                        controls={false}
                        style={{
                          width: '100%',
                          height: 175,
                          background: 'background.paper',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 175,
                          background: 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                        }}
                      >
                        <img
                          src={
                            mediaFile.type === 'image'
                              ? mediaFile.url
                              : templatePreviewsByFileType[fileType || 'docx']
                          }
                          alt=""
                          style={{
                            width: mediaFile.type !== 'image' ? '65%' : '100%',
                            height: mediaFile.type !== 'image' ? '65%' : '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 175,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.25,
                        justifyContent: 'center',
                        color: 'secondary.main',
                      }}
                    >
                      {templateHeader.format === 'IMAGE' ? (
                        <AddPhotoAlternateOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : templateHeader.format === 'VIDEO' ? (
                        <VideoFileOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : templateHeader.format === 'DOCUMENT' ? (
                        <UploadFileOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : templateHeader.format === 'LOCATION' ? (
                        <AddLocationOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : (
                        ''
                      )}
                      <Typography fontSize={15} fontWeight={700} width="100%" textAlign={'center'}>
                        העלאת קובץ
                      </Typography>
                    </Box>
                  )}
                  {['IMAGE', 'DOCUMENT', 'VIDEO'].includes(templateHeader.format) && (
                    <Box
                      component={'label'}
                      sx={{
                        pointerEvents: noUpload ? 'none' : undefined,
                        cursor: noUpload ? 'default' : 'pointer',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 100,
                      }}
                      htmlFor="template-header-media-upload"
                    />
                  )}
                </Grid>
              )}
              <Grid size={12}>
                <Typography
                  variant="body2"
                  fontSize={14}
                  whiteSpace={'pre-line'}
                  style={
                    whatsappStyle
                      ? { textAlign: 'right', width: '100%', display: 'block', direction: 'rtl' }
                      : undefined
                  }
                >
                  {varsData?.some((param) => param.name.startsWith('body'))
                    ? substituteTemplatePlaceholders(templateBody?.text, varsData, 'body')
                    : templateBody?.text}
                </Typography>
              </Grid>

              {templateFooter && (
                <Grid size={12}>
                  <Typography
                    variant="subtitle2"
                    color={brandBubbleUsesLightForeground ? undefined : 'text.secondary'}
                    fontSize={13}
                    sx={brandBubbleMutedTextColor ? { color: brandBubbleMutedTextColor } : undefined}
                    style={whatsappStyle ? { textAlign: 'right', width: '100%' } : undefined}
                  >
                    {varsData?.some((param) => param.name.startsWith('footer'))
                      ? substituteTemplatePlaceholders(templateFooter?.text, varsData, 'footer')
                      : templateFooter?.text}
                  </Typography>
                </Grid>
              )}
            </React.Fragment>
          )}
          <Grid size={12}>
            {whatsappStyle ? (
              <Box
                style={{
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 6,
                  /* LTR: שעה ואז Vים — הווי נשאר בקצה ימין של הבועה (לא היפוך RTL שדוחף אותם לשמאל) */
                  direction: 'ltr',
                }}
              >
                <Typography variant="body2" color={'text.disabled'} fontSize={12} component="span">
                  {currentDate}
                </Typography>
                <Box
                  aria-hidden
                  dir="ltr"
                  sx={{
                    position: 'relative',
                    width: 22,
                    height: 14,
                    flexShrink: 0,
                  }}
                >
                  <DoneAllIcon
                    sx={{
                      fontSize: 15,
                      color: '#53bdeb',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                    }}
                  />
                  <DoneAllIcon
                    sx={{
                      fontSize: 15,
                      color: '#53bdeb',
                      position: 'absolute',
                      left: 5,
                      top: 0,
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Grid container spacing={1} alignItems={'center'}>
                <Grid>
                  <DoneAllIcon
                    sx={{
                      fontSize: 16,
                      color: brandBubbleMutedTextColor ?? 'text.secondary',
                      verticalAlign: 'middle',
                    }}
                  />
                </Grid>
                <Grid>
                  <Typography
                    variant="body2"
                    color={brandBubbleUsesLightForeground ? undefined : 'text.disabled'}
                    fontSize={12}
                    sx={brandBubbleMutedTextColor ? { color: brandBubbleMutedTextColor } : undefined}
                  >
                    {currentDate}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>

          {templateButtons && (
            <Grid size={12}>
              {templateButtons.buttons?.map((button, index) => (
                <Box
                  key={index}
                  sx={{
                    mx: -1.25,
                    px: 1.25,
                    borderTop: `1px solid ${brandBubbleButtonDividerColor}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: 1,
                      color: brandBubbleButtonColor,
                      '& .MuiSvgIcon-root': {
                        color: 'inherit',
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'inherit',
                        textDecoration: button.type === 'URL' ? 'underline' : 'none',
                      }}
                    >
                      {button.text}
                    </Typography>
                    {button.type === 'QUICK_REPLY' && <ReplyOutlinedIcon fontSize="small" />}
                    {button.type === 'URL' && <OpenInNewOutlinedIcon fontSize="small" />}
                    {button.type === 'PHONE_NUMBER' && <PhoneOutlinedIcon fontSize="small" />}
                    {button.type === 'COPY_CODE' && <CodeOutlinedIcon fontSize="small" />}
                  </Box>
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default TemplatePreview;
